import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  IconButton,
} from '@vnetwork/ui/primitives';
import {
  MoreHorizontal,
  Pencil,
  Star,
  Trash2,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Unlink,
  UserPlus,
  LogIn,
} from 'lucide-react';

export interface ContactActionsMenuProps {
  isPrimary: boolean;
  onEdit: () => void;
  onSetPrimary: () => void;
  onDelete: () => void;
  /** Account block: rendered only when the status query resolved without error. */
  account?: {
    hasKeycloakAccount: boolean;
    hasNoAccount: boolean;
    isAccountEnabled: boolean;
    onCreateAccount: () => void;
    onSync: () => void;
    onEnable: () => void;
    onDisable: () => void;
    onUnlink: () => void;
    /** Impersonate — present only when whois is valid AND the partner URL is set. */
    onImpersonate?: () => void;
  };
  busy?: boolean;
}

/**
 * Row-actions "⋯" menu for a contact card. Mirrors the legacy
 * `contact-actions-menu`: Set-as-primary / Edit / Delete, then an account block
 * (Create / Enable-Disable / Unlink / Sync / Impersonate) that only renders when
 * the account-status query succeeded (`account` prop present).
 */
export function ContactActionsMenu({
  isPrimary,
  onEdit,
  onSetPrimary,
  onDelete,
  account,
  busy,
}: ContactActionsMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton variant="ghost" size="sm" aria-label="Contact actions" disabled={busy}>
          <MoreHorizontal size={16} />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!isPrimary ? (
          <DropdownMenuItem onSelect={onSetPrimary}>
            <Star size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
            Set as primary
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem tone="danger" onSelect={onDelete}>
          <Trash2 size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
          Delete
        </DropdownMenuItem>

        {account ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Account</DropdownMenuLabel>
            {account.hasNoAccount ? (
              <DropdownMenuItem onSelect={account.onCreateAccount}>
                <UserPlus size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                Create account
              </DropdownMenuItem>
            ) : null}
            {account.hasKeycloakAccount ? (
              <>
                {account.isAccountEnabled ? (
                  <DropdownMenuItem tone="danger" onSelect={account.onDisable}>
                    <ShieldOff size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                    Disable account
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={account.onEnable}>
                    <ShieldCheck size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                    Enable account
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={account.onUnlink}>
                  <Unlink size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                  Unlink account
                </DropdownMenuItem>
              </>
            ) : null}
            <DropdownMenuItem onSelect={account.onSync}>
              <RefreshCw size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
              Sync account
            </DropdownMenuItem>
            {account.onImpersonate ? (
              <DropdownMenuItem onSelect={account.onImpersonate}>
                <LogIn size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                Impersonate
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ContactActionsMenu;
